<script lang="ts">
import { AuthPopover, UserRoles } from '@onereach/auth-ui-module';
import { ServiceNavigationHeader, BreadcrumbItem } from '@onereach/service-navigation';
import { OrNotificationV3, OrToastContainerV3 } from '@onereach/ui-components';
import { useVModel } from '@vueuse/core';
import { defineComponent, PropType } from 'vue';

import pkg from '../../package.json';

export default defineComponent({
  name: 'AppPage',
  components: {
    AuthPopover,
    ServiceNavigationHeader,
    OrNotificationV3,
    OrToastContainerV3,
  },
  props: {
    token: {
      type: String,
      required: false,
      default: null,
    },
    accountId: {
      type: String,
      required: false,
      default: null,
    },
    config: {
      type: Object,
      required: true,
    },
    showError: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorText: {
      type: String,
      required: false,
      default: null,
    },
    userRole: {
      type: String as PropType<UserRoles>,
      required: true,
    },
  },
  emits: ['update:showError'],
  setup(props, { emit }) {
    return {
      serviceTranslations: pkg.projectConfig.translations,
      isErrorShown: useVModel(props, 'showError', emit),
    };
  },
  data() {
    return {
      label: this.$t('title.queueManager'),
    };
  },
  computed: {
    breadcrumbs(): BreadcrumbItem[] {
      return this.$route.matched
        .map(({ meta }) => meta.breadcrumb)
        .filter(Boolean)
        .map((breadcrumb) => {
          return typeof breadcrumb === 'function' ? breadcrumb.call(this) : breadcrumb;
        });
    },
  },
});
</script>
<template>
  <div class="h-full gap-md layout-column bg-surface-1 dark:bg-surface-1-dark">
    <ServiceNavigationHeader
      :config="config"
      :label="label"
      :token="token"
      :breadcrumb-items="breadcrumbs"
      :account-id="accountId"
      :user-role="userRole"
      :service-translations="serviceTranslations"
    >
      <template #account>
        <AuthPopover
          :token="token"
          :dark-mode-options="{
            attribute: 'data-theme',
            valueDark: 'dark',
          }"
        />
      </template>
    </ServiceNavigationHeader>
    <OrNotificationV3
      v-if="isErrorShown"
      class="absolute inset-x-0 top-14 m-auto z-[100]"
      variant="error"
      @close="isErrorShown=false"
    >
      {{ errorText }}
    </OrNotificationV3>
    <div
      id="page-content-container"
      class="relative min-h-[0] h-full w-full layout-column"
    >
      <div
        id="page-content"
        class="relative overflow-auto h-full w-full m-auto layout-column m-auto md:p-xl p-md"
        data-test="page-content"
      >
        <slot />
      </div>
    </div>
    <OrToastContainerV3 />
  </div>
</template>
